import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../../utils/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar as CalendarPrimitive } from "../../components/ui/calendar";
import {
  Loader2, ShieldCheck, AlertCircle, Building2, Calendar,
  DollarSign, Download, CheckCircle2, XCircle, PenLine, Trash2,
} from "lucide-react";
import { cn } from "../../components/ui/utils";
import { formatSydneyDateLong } from "../../utils/helpers";
import html2pdf from "html2pdf.js";

type ViewState = "loading" | "code" | "offer" | "error" | "expired" | "accepted" | "rejected";

interface OfferData {
  id: number;
  status: string;
  salaryOffered: string | null;
  startDate: string | null;
  expiryDate: string | null;
  offerLetterContent: string | null;
  sentAt: string | null;
  candidateSignature: string | null;
  candidateSignedDate: string | null;
  candidate: { firstName: string; lastName: string; email: string } | null;
  positionTitle: string;
  companyName: string;
}

export default function OfferView() {
  const { id } = useParams<{ id: string }>();
  const offerId = parseInt(id || "");

  const [viewState, setViewState] = useState<ViewState>("loading");
  const [code, setCode] = useState("");
  const [offer, setOffer] = useState<OfferData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRequesting, setIsRequesting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const mounted = useRef(false);

  // Accept form state
  const [signedDate, setSignedDate] = useState("");
  const [signatureData, setSignatureData] = useState("");
  const [isAccepting, setIsAccepting] = useState(false);

  // Reject form state
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);

  // Signature canvas ref
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const documentRef = useRef<HTMLDivElement>(null);

  // Check for dev mode shortcut (?dev=true)
  const isDevMode = new URLSearchParams(window.location.search).get("dev") === "true";

  // Auto-request code on mount (or skip to offer in dev mode)
  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    if (isNaN(offerId)) {
      setErrorMessage("Invalid offer link");
      setViewState("error");
      return;
    }
    if (isDevMode) {
      loadDevOffer();
    } else {
      requestCode();
    }
  }, [offerId]);

  const loadDevOffer = async () => {
    try {
      const response = await api.get<{ success: boolean; offer: OfferData }>(
        `/api/offers/${offerId}/dev-view`,
        { skipAuthHandler: true }
      );
      setOffer(response.offer);
      // Always show the letter view — it hides the accept/decline actions
      // for offers that have already been accepted or declined.
      setViewState("offer");
    } catch (error: any) {
      const msg = error?.data?.message || error?.message || "Unable to load offer";
      setErrorMessage(msg);
      setViewState("error");
    }
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Set default signed date when offer loads
  useEffect(() => {
    if (offer) {
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      setSignedDate(`${y}-${m}-${d}`);
    }
  }, [offer]);

  const requestCode = async () => {
    setIsRequesting(true);
    setErrorMessage("");
    try {
      await api.post(`/api/offers/${offerId}/request-code`, {}, { skipAuthHandler: true });
      setCodeSent(true);
      setResendCooldown(60);
      setViewState("code");
    } catch (error: any) {
      const msg = error?.data?.message || error?.message || "Unable to send verification code";
      setErrorMessage(msg);
      setViewState(msg.toLowerCase().includes("expired") ? "expired" : "error");
    } finally {
      setIsRequesting(false);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) return;
    setIsVerifying(true);
    setErrorMessage("");
    try {
      const response = await api.post<{ success: boolean; offer: OfferData }>(
        `/api/offers/${offerId}/verify`,
        { code },
        { skipAuthHandler: true }
      );
      setOffer(response.offer);
      // Always show the letter view — it hides the accept/decline actions
      // for offers that have already been accepted or declined.
      setViewState("offer");
    } catch (error: any) {
      const msg = error?.data?.message || error?.message || "Invalid verification code";
      setErrorMessage(msg);
      if (msg.toLowerCase().includes("expired")) setViewState("expired");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleAccept = async () => {
    if (!signedDate || !signatureData) return;
    setIsAccepting(true);
    try {
      await api.post(`/api/offers/${offerId}/accept`, { signedDate, signature: signatureData }, { skipAuthHandler: true });
      setViewState("accepted");
    } catch (error: any) {
      setErrorMessage(error?.data?.message || error?.message || "Failed to accept offer");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim() || rejectionReason.trim().length < 3) return;
    setIsRejecting(true);
    try {
      await api.post(`/api/offers/${offerId}/reject`, { rejectionReason: rejectionReason.trim() }, { skipAuthHandler: true });
      setViewState("rejected");
    } catch (error: any) {
      setErrorMessage(error?.data?.message || error?.message || "Failed to reject offer");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleDownload = async () => {
    if (!documentRef.current) return;
    setIsDownloading(true);
    try {
      const opt = {
        margin: 15,
        filename: `Offer-${offer?.candidate?.lastName || "Letter"}.pdf`,
        image: { type: "jpeg" as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm" as const, format: "a4" as const, orientation: "portrait" as const },
      };
      await html2pdf().set(opt).from(documentRef.current).save();
    } catch (err) {
      console.error("Download error:", err);
      window.print();
    } finally {
      setIsDownloading(false);
    }
  };

  // Signature canvas handlers
  const getCanvasPoint = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDrawing.current = true;
    const { x, y } = getCanvasPoint(e);
    const ctx = canvasRef.current!.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const { x, y } = getCanvasPoint(e);
    const ctx = canvasRef.current!.getContext("2d")!;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    const canvas = canvasRef.current;
    if (canvas) setSignatureData(canvas.toDataURL("image/png"));
  }, []);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData("");
  };

  // Init canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F1E3D";
  }, [viewState]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    // sentAt is an ISO timestamp (contains 'T'), startDate/expiryDate are date-only
    const d = dateStr.includes("T") ? new Date(dateStr) : new Date(dateStr + "T00:00:00");
    if (isNaN(d.getTime())) return "—";
    return formatSydneyDateLong(d) || "—";
  };

  const formatCurrency = (amount: string | null) => {
    if (!amount) return "—";
    const num = Number(amount);
    if (isNaN(num)) return amount;
    return `$${num.toLocaleString()}`;
  };

  if (viewState === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#0F1E3D]" />
          <p className="text-slate-600 text-sm">Preparing your offer…</p>
        </div>
      </div>
    );
  }

  if (viewState === "error" || viewState === "expired") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">
              {viewState === "expired" ? "Offer Expired" : "Unable to Access Offer"}
            </h2>
            <p className="text-sm text-slate-500 mt-2">{errorMessage}</p>
            <p className="text-xs text-slate-400 mt-4">If you believe this is an error, please contact the HR team.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (viewState === "code") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <div className="p-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#0F1E3D]">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Verify Your Code</h2>
            <p className="text-sm text-slate-500 mt-1">
              {codeSent ? "We've sent a 6-digit verification code to your email." : "Requesting verification code…"}
            </p>
            <div className="flex justify-center mt-6">
              <InputOTP maxLength={6} value={code} onChange={setCode} onComplete={verifyCode} disabled={isVerifying}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                  <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button onClick={verifyCode} disabled={code.length !== 6 || isVerifying} className="w-full mt-6 bg-[#0F1E3D] hover:bg-[#1a2d4f] text-white">
              {isVerifying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying…</> : "Verify & View Offer"}
            </Button>
            {errorMessage && <p className="text-center text-sm text-red-600 mt-3">{errorMessage}</p>}
            <div className="text-center mt-4">
              <button onClick={requestCode} disabled={resendCooldown > 0 || isRequesting}
                className={cn("text-sm font-medium", resendCooldown > 0 || isRequesting ? "text-slate-400 cursor-not-allowed" : "text-[#0F1E3D] hover:underline")}>
                {isRequesting ? "Sending…" : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (viewState === "accepted") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-7 w-7 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Offer Accepted</h2>
            <p className="text-sm text-slate-500 mt-2">Thank you! You have successfully accepted this offer. The HR team has been notified.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (viewState === "rejected") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
              <XCircle className="h-7 w-7 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Offer Declined</h2>
            <p className="text-sm text-slate-500 mt-2">Thank you for your response. The HR team has been notified of your decision.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (viewState === "offer" && offer) {
    const candidateName = offer.candidate ? `${offer.candidate.firstName} ${offer.candidate.lastName}` : "Candidate";
    const isAcceptedOrRejected = offer.status === "ACCEPTED" || offer.status === "REJECTED";

    return (
      <div className="min-h-screen bg-slate-100 py-6 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#0F1E3D]" />
              <span className="text-lg font-bold text-[#0F1E3D]">{offer.companyName}</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownload} disabled={isDownloading}
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-bold h-9 px-4">
              {isDownloading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-2 h-3.5 w-3.5" />}
              Download PDF
            </Button>
          </div>

          {/* Document */}
          <div ref={documentRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 pb-16 sm:pb-20 max-w-[210mm] mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-slate-900">Offer of Employment</h1>
              <p className="text-slate-500 text-sm mt-1">Sent {offer.sentAt ? formatDate(offer.sentAt) : "—"}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8 bg-slate-50 rounded-lg p-4 border border-slate-100">
              <SummaryRow label="Candidate" value={candidateName} />
              <SummaryRow label="Position" value={offer.positionTitle || "—"} />
              <SummaryRow label="Salary" value={formatCurrency(offer.salaryOffered)} />
              <SummaryRow label="Start Date" value={formatDate(offer.startDate)} />
              <SummaryRow label="Offer Expires" value={formatDate(offer.expiryDate)} />
              <SummaryRow label="Company" value={offer.companyName} />
            </div>

            {offer.offerLetterContent ? (
              <div
                className="prose prose-sm max-w-none text-slate-800 text-[15px] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: offer.offerLetterContent }}
              />
            ) : (
              <p className="text-slate-800 text-[15px] leading-relaxed">No offer letter content available.</p>
            )}

            {(signatureData || offer.candidateSignature) && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Digital Signature</p>
                <img src={signatureData || offer.candidateSignature || ""} alt="Signature" className="h-16 object-contain" />
                <p className="text-xs text-slate-400 mt-1">Signed on {formatDate(offer.candidateSignedDate || signedDate)}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          {!isAcceptedOrRejected && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Accept */}
              <Card className="border-green-100">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <h3 className="font-bold text-slate-900">Accept Offer</h3>
                  </div>
                  <p className="text-sm text-slate-500">Sign and date below to accept this offer.</p>

                  {/* Date */}
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Date <span className="text-red-500">*</span></label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={`w-full justify-start text-left font-normal h-10 rounded-xl text-sm bg-gray-50 border-gray-200 hover:bg-gray-100 ${!signedDate ? "text-gray-400" : "text-gray-900"}`}>
                          <Calendar className="mr-2 h-4 w-4 text-gray-400 shrink-0" />
                          {signedDate ? formatDate(signedDate) : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarPrimitive
                          mode="single"
                          selected={signedDate ? new Date(signedDate + "T00:00:00") : undefined}
                          onSelect={(date) => {
                            if (date) {
                              const y = date.getFullYear();
                              const m = String(date.getMonth() + 1).padStart(2, "0");
                              const d = String(date.getDate()).padStart(2, "0");
                              setSignedDate(`${y}-${m}-${d}`);
                            }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Signature */}
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Digital Signature <span className="text-red-500">*</span></label>
                    <div className="relative border border-gray-200 rounded-xl bg-white overflow-hidden">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={120}
                        className="w-full h-[120px] cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                      {!signatureData && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="flex items-center gap-2 text-gray-300">
                            <PenLine className="h-4 w-4" />
                            <span className="text-xs font-medium">Sign here</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {signatureData && (
                      <button onClick={clearSignature} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 mt-2 font-medium">
                        <Trash2 className="h-3 w-3" /> Clear signature
                      </button>
                    )}
                  </div>

                  <Button
                    onClick={handleAccept}
                    disabled={!signedDate || !signatureData || isAccepting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-10 rounded-xl text-sm font-bold"
                  >
                    {isAccepting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Accepting…</> : "Accept Offer"}
                  </Button>
                </CardContent>
              </Card>

              {/* Reject */}
              <Card className="border-red-100">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" />
                    <h3 className="font-bold text-slate-900">Decline Offer</h3>
                  </div>
                  <p className="text-sm text-slate-500">Please let us know why you are declining this offer.</p>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter your reason..."
                    rows={4}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm font-medium focus:ring-2 focus:ring-red-100 focus:border-red-300 outline-none resize-none"
                  />
                  <Button
                    onClick={handleReject}
                    disabled={!rejectionReason.trim() || rejectionReason.trim().length < 3 || isRejecting}
                    variant="outline"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 h-10 rounded-xl text-sm font-bold"
                  >
                    {isRejecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Declining…</> : "Decline Offer"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {errorMessage && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
              <p className="text-sm text-red-600">{errorMessage}</p>
            </div>
          )}

          <p className="text-center text-xs text-slate-400 pb-8">
            This is a secure document intended for {candidateName} only.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}
